import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import noop from 'lodash/noop';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Flyout } from '@els/els-react--flyout';
import { Table } from '@els/els-react--table';
import Loader from '@Components/Loader';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { RxSocketUrls } from '@Constants/CommonConstants';
import * as Results from '@Services/Results';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import Similarity from '@Services/Similarity';
import UserDatabases from '@Services/UserDatabases';

function currentDatabase(databaseId) {
  return { databaseId, dbName: UserDatabases.getDbName(databaseId) };
}

function itemColumnId(item, index) {
  return `find-similar-reaction-${item?.queryId || kebabCase(item?.description) || index}`;
}

function ReactionFindSimilarContent({ metadata, reaction, databaseId }) {
  const resolvedDatabaseId = reaction?.databaseId || databaseId || metadata?.databaseId;
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedDatabase, setSelectedDatabase] = useState(() => currentDatabase(resolvedDatabaseId));
  const dataRef = useRef(null);
  const selectedDatabaseRef = useRef(selectedDatabase);
  const searchIdRef = useRef(null);

  const updateResults = useCallback((result) => {
    const payload = get(result, 'payload');
    if (!payload || Array.isArray(payload)) return;

    searchIdRef.current = searchIdRef.current || payload.searchId;
    const previous = dataRef.current;
    let next = null;
    if (payload.items) {
      next = payload;
    } else if (previous?.items) {
      next = { ...previous, items: previous.items.map((item) => (item?.queryId === payload.queryId ? { ...item, ...payload } : item)) };
    }

    if (!next) return;
    dataRef.current = next;
    setData(next);

    const selectedItems = next.items?.filter((item) => item?.databaseId === selectedDatabaseRef.current.databaseId) || [];
    if (!selectedItems.length && next.items?.[0]) {
      const nextDatabase = currentDatabase(next.items[0].databaseId);
      selectedDatabaseRef.current = nextDatabase;
      setSelectedDatabase(nextDatabase);
    }
    if (selectedItems.some((item) => typeof item?.resultSize !== 'undefined')) setIsLoading(false);
  }, []);

  useEffect(() => {
    dataRef.current = null;
    searchIdRef.current = null;
    setData(null);
    const nextDatabase = currentDatabase(resolvedDatabaseId);
    selectedDatabaseRef.current = nextDatabase;
    setSelectedDatabase(nextDatabase);
    setIsLoading(true);

    const subscription = ResultsDataManage.getSimilar(reaction.id, resolvedDatabaseId, null, RxSocketUrls.SimilarReactions).subscribe({
      next: updateResults,
      error: (error) => {
        Logger.error(error);
        setIsLoading(false);
      },
    });
    return () => {
      subscription.unsubscribe();
      if (searchIdRef.current) Results.cancelSearch(searchIdRef.current);
    };
  }, [reaction.id, resolvedDatabaseId, updateResults]);

  const items = useMemo(
    () => (data?.items || []).filter((item) => !selectedDatabase.databaseId || item?.databaseId === selectedDatabase.databaseId),
    [data, selectedDatabase.databaseId]
  );
  const image = data?.image;
  const goTo = useCallback(
    (result) =>
      Similarity.goToSimilarReactionResult(
        reaction.id,
        searchIdRef.current || result?.searchId,
        resolvedDatabaseId,
        metadata?.hitSetKey,
        reaction.rxnFile,
        result
      ),
    [metadata?.hitSetKey, reaction.id, reaction.rxnFile, resolvedDatabaseId]
  );

  const columns = useMemo(
    () => [
      { value: 'queryReaction', label: image?.label },
      ...items.map((item, index) => ({
        value: itemColumnId(item, index),
        label: (
          <div className="u-els-display-flex o-els-flex-layout--middle">
            <span>{item?.description}</span>
            {item?.help && (
              <Flyout
                className="u-els-margin-left-1o4"
                flyout={<div className="u-els-font-size-base u-els-color-text">{item.help}</div>}
                shouldCloseOnESC
                showCloseButton
                placement="bottom-start"
                pointerType="primary">
                <ButtonWithIcon
                  data-e2e={`find-similar-help-${kebabCase(item.description)}`}
                  sprite="HelpOutlineCircle"
                  iconOnly
                  type="link"
                  linkIconSize="xs"
                  aria-label={`Show help for ${item.description}`}
                  onClick={noop}>
                  {item.description}
                </ButtonWithIcon>
              </Flyout>
            )}
          </div>
        ),
      })),
    ],
    [image?.label, items]
  );

  const row = useMemo(() => {
    const result = {
      id: 'find-similar-reaction-row',
      queryReaction: image?.url && <img src={image.url} alt="Find similar query reaction" />,
    };
    items.forEach((item, index) => {
      const count = item?.resultSize;
      result[itemColumnId(item, index)] = count ? (
        <Button data-e2e="find-similar-card-go-to" type="link" onClick={() => goTo(item)}>
          {count.toLocaleString()}
        </Button>
      ) : (
        <span>{count}</span>
      );
    });
    return [result];
  }, [goTo, image?.url, items]);

  return (
    <div data-testid="find-similar-content">
      {isLoading && (
        <div className="u-els-margin-bottom">
          <Loader startProgress={0} />
        </div>
      )}
      {data && (
        <Table
          a11y={{ name: 'Find similar reactions', description: 'Find similar reactions for the query reaction' }}
          columns={columns}
          data={row}
          highlightHeader
          id="find-similar-reactions-table"
          rowKey="id"
          subtleDividers
          useSemantic
          verticalDividers
          renderDatum={(renderedRows, { columns: renderColumns = [] }) =>
            (renderedRows || []).map((tableRow) => (
              <tr key={tableRow.id} data-testid={tableRow.id} className="c-els-table__row no-hover-background">
                {renderColumns.map((column) => {
                  const columnId = typeof column === 'object' ? column.value : column;
                  return (
                    <td key={columnId} className="c-els-table__cell">
                      {tableRow[columnId]}
                    </td>
                  );
                })}
              </tr>
            ))
          }
        />
      )}
    </div>
  );
}

ReactionFindSimilarContent.propTypes = {
  metadata: MetadataPropTypesShape,
  reaction: PropTypes.object.isRequired,
  databaseId: PropTypes.string,
};

export default ReactionFindSimilarContent;
